import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../lib/api';
import {
  Plus, Eye, Rocket, AlertCircle, CheckCircle2, Loader2, Sparkles,
  ShieldCheck, Layers,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const SNIPPET_LANGS = {
  terraform: 'python',
  pulumi: 'python',
  ansible: 'python',
  arm: 'python',
  catalog: 'python',
};

const SnippetBlock = ({ label, code, lang = 'python' }) => (
  <div className="rounded-lg border border-slate-700 bg-slate-900/60 overflow-hidden" data-testid={`snippet-${label}`}>
    <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700 bg-slate-900/80">
      <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
      <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-600">{lang}</Badge>
    </div>
    <pre className="text-xs text-slate-200 px-3 py-2 overflow-x-auto whitespace-pre">
{code}
    </pre>
  </div>
);

const StatPill = ({ label, value, color = 'text-emerald-400' }) => (
  <div className="rounded-lg border border-slate-700 bg-slate-900/40 px-4 py-3 flex flex-col">
    <span className="text-[11px] uppercase tracking-widest text-slate-400">{label}</span>
    <span className={`text-2xl font-semibold ${color}`}>{value}</span>
  </div>
);

export default function AddService() {
  const [form, setForm] = useState({
    name: '',
    provider: 'aws',
    category: 'networking',
    display_name: '',
    deps: '',
    tf_resource: '',
    pulumi_ctor: '',
    ansible_module: '',
    arm_type: '',
    arm_api_version: '',
    force: false,
  });
  const [options, setOptions] = useState({ providers: ['aws', 'azure', 'gcp'], categories: [] });
  const [preview, setPreview] = useState(null);
  const [scaffoldResult, setScaffoldResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }));

  // Load options + admin guard
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/admin/iac/options`, { withCredentials: true })
      .then(r => { if (mounted) setOptions(r.data); })
      .catch(err => {
        if (!mounted) return;
        if (err.response?.status === 403) setDenied(true);
        else if (err.response?.status === 401) setDenied(true);
      });
    return () => { mounted = false; };
  }, []);

  const validName = useMemo(() => /^[a-z][a-z0-9_]*$/.test(form.name), [form.name]);
  const azureMissingArm = form.provider === 'azure' && (!form.arm_type || !form.arm_api_version);
  const canSubmit = validName && !azureMissingArm && !busy;

  const buildPayload = () => ({
    name: form.name.trim(),
    provider: form.provider,
    category: form.category,
    display_name: form.display_name.trim() || null,
    deps: form.deps.split(',').map(s => s.trim()).filter(Boolean),
    tf_resource: form.tf_resource.trim() || null,
    pulumi_ctor: form.pulumi_ctor.trim() || null,
    ansible_module: form.ansible_module.trim() || null,
    arm_type: form.provider === 'azure' ? form.arm_type.trim() : null,
    arm_api_version: form.provider === 'azure' ? form.arm_api_version.trim() : null,
    force: form.force,
  });

  const handlePreview = async () => {
    setBusy(true); setError(null); setScaffoldResult(null);
    try {
      const r = await axios.post(`${API}/admin/iac/preview`, buildPayload(), { withCredentials: true });
      setPreview(r.data);
      toast.success('Preview generated — review snippets below');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setError(msg);
      toast.error(`Preview failed: ${msg}`);
    } finally { setBusy(false); }
  };

  const handleScaffold = async () => {
    setBusy(true); setError(null);
    try {
      const r = await axios.post(`${API}/admin/iac/scaffold`, buildPayload(), { withCredentials: true });
      setScaffoldResult(r.data);
      setPreview(r.data);  // mirror snippets
      toast.success(`Scaffolded ${r.data.spec.provider}/${r.data.spec.name} — matrix ${r.data.matrix?.real ?? '?'} real`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setError(msg);
      if (err.response?.status === 409) {
        toast.error('Service already exists. Toggle "Overwrite" and retry.');
      } else {
        toast.error(`Scaffold failed: ${msg}`);
      }
    } finally { setBusy(false); }
  };

  if (denied) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="add-service-denied">
        <Card className="bg-slate-900/60 border-slate-700">
          <CardContent className="p-8 text-center">
            <ShieldCheck className="w-12 h-12 text-rose-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Admin access required</h2>
            <p className="text-slate-400">This page is only available to admin users.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="add-service-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-emerald-400" />
            Add IaC Service
          </h1>
          <p className="text-slate-400 mt-2 max-w-2xl">
            Scaffold a new cloud service across all four IaC generators
            (Terraform, Pulumi, ARM, Ansible) plus the catalog — in a single
            click. The 918+ matrix regression test runs automatically.
          </p>
        </div>
        <Badge variant="outline" className="text-emerald-400 border-emerald-700">
          <ShieldCheck className="w-3 h-3 mr-1" /> Admin only
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form column */}
        <Card className="bg-slate-900/60 border-slate-700" data-testid="add-service-form">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Layers className="w-5 h-5 text-cyan-400" />
              Service definition
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Provider + name + category */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="provider" className="text-slate-300">Provider *</Label>
                <Select value={form.provider} onValueChange={update('provider')}>
                  <SelectTrigger data-testid="select-provider"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.providers.map(p => (
                      <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="category" className="text-slate-300">Category</Label>
                <Select value={form.category} onValueChange={update('category')}>
                  <SelectTrigger data-testid="select-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(options.categories.length ? options.categories : ['compute','storage','database','networking','security','monitoring','integration','data','container','other']).map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="name" className="text-slate-300">Service ID * <span className="text-slate-500 text-xs ml-1">(snake_case, e.g. <code>vpc_lattice</code>)</span></Label>
              <Input
                id="name"
                data-testid="input-name"
                value={form.name}
                onChange={update('name')}
                placeholder="vpc_lattice"
                className={!form.name || validName ? '' : 'border-rose-500'}
              />
              {!validName && form.name && (
                <p className="text-xs text-rose-400 mt-1">Must start with a letter; lowercase + digits + underscore only.</p>
              )}
            </div>

            <div>
              <Label className="text-slate-300">Display name <span className="text-slate-500 text-xs">(auto from id if blank)</span></Label>
              <Input
                data-testid="input-display"
                value={form.display_name}
                onChange={update('display_name')}
                placeholder="VPC Lattice Service Network"
              />
            </div>

            <div>
              <Label className="text-slate-300">Dependencies <span className="text-slate-500 text-xs">(comma-separated catalog ids)</span></Label>
              <Input
                data-testid="input-deps"
                value={form.deps}
                onChange={update('deps')}
                placeholder="vpc, subnet"
              />
            </div>

            <div className="border-t border-slate-700 pt-4">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Generator overrides (optional — auto-inferred when blank)</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-300 text-sm">Terraform resource type</Label>
                  <Input data-testid="input-tf"
                    value={form.tf_resource}
                    onChange={update('tf_resource')}
                    placeholder={form.provider === 'aws' ? 'aws_vpclattice_service_network'
                      : form.provider === 'azure' ? 'azurerm_xxx' : 'google_xxx'} />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Pulumi constructor</Label>
                  <Input data-testid="input-pulumi"
                    value={form.pulumi_ctor}
                    onChange={update('pulumi_ctor')}
                    placeholder="aws.vpclattice.ServiceNetwork" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Ansible module FQCN</Label>
                  <Input data-testid="input-ansible"
                    value={form.ansible_module}
                    onChange={update('ansible_module')}
                    placeholder="community.aws.vpclattice_service_network" />
                </div>
              </div>
            </div>

            {form.provider === 'azure' && (
              <div className="border-t border-slate-700 pt-4 space-y-3" data-testid="azure-arm-block">
                <p className="text-xs uppercase tracking-wider text-amber-400 mb-1">ARM (required for Azure)</p>
                <div>
                  <Label className="text-slate-300 text-sm">ARM resource type *</Label>
                  <Input data-testid="input-arm-type"
                    value={form.arm_type}
                    onChange={update('arm_type')}
                    placeholder="Microsoft.App/managedEnvironments" />
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">ARM API version *</Label>
                  <Input data-testid="input-arm-version"
                    value={form.arm_api_version}
                    onChange={update('arm_api_version')}
                    placeholder="2023-05-01" />
                </div>
                {azureMissingArm && (
                  <p className="text-xs text-amber-400">Both ARM fields are required for Azure services.</p>
                )}
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <Switch
                id="force"
                data-testid="switch-force"
                checked={form.force}
                onCheckedChange={(v) => setForm(f => ({ ...f, force: v }))}
              />
              <Label htmlFor="force" className="text-slate-300 text-sm cursor-pointer">
                Overwrite if entry already exists
              </Label>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handlePreview} disabled={!canSubmit} variant="outline"
                className="flex-1 border-slate-600 text-slate-200 hover:bg-slate-800"
                data-testid="btn-preview">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                Preview snippets
              </Button>
              <Button onClick={handleScaffold} disabled={!canSubmit}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500"
                data-testid="btn-scaffold">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                Scaffold + run matrix
              </Button>
            </div>

            {error && (
              <div className="rounded-lg border border-rose-700 bg-rose-950/40 p-3 text-rose-200 text-sm flex gap-2"
                   data-testid="add-service-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview column */}
        <Card className="bg-slate-900/60 border-slate-700" data-testid="preview-pane">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              {scaffoldResult ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              ) : (
                <Eye className="w-5 h-5 text-cyan-400" />
              )}
              {scaffoldResult ? 'Scaffold result' : 'Preview'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!preview && (
              <div className="text-center py-12 text-slate-500">
                <Plus className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Fill the form on the left and click <strong>Preview snippets</strong> to see what will be inserted.</p>
              </div>
            )}

            {preview && (
              <>
                {/* Resolved spec */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="text-slate-400">Provider/Name</div>
                  <div className="text-slate-100 font-mono">{preview.spec.provider}/{preview.spec.name}</div>
                  <div className="text-slate-400">Display name</div>
                  <div className="text-slate-100">{preview.spec.display_name}</div>
                  <div className="text-slate-400">Category</div>
                  <div className="text-slate-100">{preview.spec.category}</div>
                  <div className="text-slate-400">Dependencies</div>
                  <div className="text-slate-100 font-mono">{preview.spec.deps?.length ? preview.spec.deps.join(', ') : '(none)'}</div>
                  <div className="text-slate-400">Terraform</div>
                  <div className="text-slate-100 font-mono">{preview.spec.tf_resource}</div>
                  <div className="text-slate-400">Pulumi</div>
                  <div className="text-slate-100 font-mono">{preview.spec.pulumi_ctor}</div>
                  <div className="text-slate-400">Ansible</div>
                  <div className="text-slate-100 font-mono">{preview.spec.ansible_module}</div>
                  {preview.spec.arm_type && (
                    <>
                      <div className="text-slate-400">ARM</div>
                      <div className="text-slate-100 font-mono">{preview.spec.arm_type}@{preview.spec.arm_api_version}</div>
                    </>
                  )}
                </div>

                {/* Matrix result if scaffolded */}
                {scaffoldResult?.matrix && (
                  <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-700">
                    <StatPill label="Combos" value={scaffoldResult.matrix.combos ?? scaffoldResult.matrix.real} color="text-emerald-400" />
                    <StatPill label="Real" value={scaffoldResult.matrix.real} color="text-emerald-400" />
                    <StatPill label="Stubs" value={scaffoldResult.matrix.stub ?? 0} color={(scaffoldResult.matrix.stub ?? 0) === 0 ? 'text-emerald-400' : 'text-rose-400'} />
                  </div>
                )}

                {scaffoldResult?.files_changed?.length > 0 && (
                  <div className="rounded-lg border border-emerald-700 bg-emerald-950/30 p-3 text-emerald-200 text-xs"
                       data-testid="files-changed">
                    <div className="font-semibold mb-1 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" /> Files written:
                    </div>
                    <ul className="font-mono pl-5 list-disc space-y-0.5">
                      {scaffoldResult.files_changed.map(f => <li key={f}>{f}</li>)}
                    </ul>
                  </div>
                )}

                {/* Snippets */}
                <div className="space-y-3 pt-3 border-t border-slate-700">
                  <p className="text-xs uppercase tracking-wider text-slate-500">Generated snippets</p>
                  {Object.entries(preview.snippets || {}).map(([k, v]) => (
                    <SnippetBlock key={k} label={k} code={v} lang={SNIPPET_LANGS[k] || 'python'} />
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
